/**
 * Form Error Handler Utilities
 * 
 * Provides centralized error handling for form validation errors,
 * including Zod validation errors from React Hook Form.
 * 
 * Features:
 * - Catches ZodError and displays user-friendly toast messages
 * - Formats nested error paths for clarity
 * - Integrates with react-hot-toast
 * - Provides hooks for easy integration with forms
 * 
 * @packageDocumentation
 */

import { ZodError, type ZodIssue } from 'zod';
import toast from 'react-hot-toast';
import { FieldErrors } from 'react-hook-form';

interface HttpErrorLike {
  message: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
}

function isHttpErrorLike(error: unknown): error is HttpErrorLike {
  if (!(error instanceof Error)) {
    return false;
  }

  if (!('response' in error)) {
    return false;
  }

  const maybeResponse = (error as HttpErrorLike).response;
  return maybeResponse === undefined || typeof maybeResponse === 'object';
}

// ========================================
// ERROR FORMATTING
// ========================================

/**
 * Formats a Zod issue path into a human-readable field name.
 * 
 * @example
 * formatPath(['outboundTlsConfig', 'minVersion']) // "Outbound TLS Config > Min Version"
 * formatPath(['mediaConfig', 'rtpPortRange', 'min']) // "Media Config > RTP Port Range > Min"
 */
export function formatPath(path: readonly (string | number | symbol)[]): string {
  return path
    .map(segment => {
      if (typeof segment === 'number') {
        return `#${segment + 1}`;
      }
      if (typeof segment === 'symbol') {
        return String(segment);
      }
      // Convert camelCase to Title Case with spaces
      return segment
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    })
    .join(' > ');
}

/**
 * Formats a Zod issue into a user-friendly error message.
 */
export function formatZodIssue(issue: ZodIssue): string {
  const path = issue.path.length > 0 ? formatPath(issue.path) : 'Form';
  return `${path}: ${issue.message}`;
}

/**
 * Formats all Zod issues into a single message string.
 */
export function formatZodErrors(error: ZodError<unknown>): string {
  return error.issues.map(formatZodIssue).join('\n');
}

// ========================================
// TOAST NOTIFICATIONS
// ========================================

/**
 * Shows a toast notification for a ZodError.
 * Displays each error on a separate line for clarity.
 */
export function showZodErrorToast(error: ZodError<unknown>): void {
  const messages = error.issues.map(formatZodIssue);
  
  // For single error, show simple toast
  if (messages.length === 1) {
    toast.error(messages[0], {
      duration: 5000,
      style: {
        maxWidth: '500px',
      },
    });
    return;
  }
  
  // For multiple errors, show count in title with details
  toast.error(
    `Validation failed (${messages.length} errors):\n${messages.slice(0, 3).join('\n')}${messages.length > 3 ? `\n...and ${messages.length - 3} more` : ''}`,
    {
      duration: 7000,
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
      },
    }
  );
}

/**
 * Shows a toast notification for React Hook Form field errors.
 */
export function showFieldErrorsToast(errors: FieldErrors): void {
  const messages: string[] = [];
  
  function extractErrors(obj: FieldErrors, path: string[] = []): void {
    for (const [key, value] of Object.entries(obj)) {
      if (!value) continue;
      
      const currentPath = [...path, key];
      
      // Check if this is a field error (has message property)
      if ('message' in value && typeof value.message === 'string') {
        messages.push(`${formatPath(currentPath)}: ${value.message}`);
      } else if (typeof value === 'object') {
        // Recurse into nested errors
        extractErrors(value as FieldErrors, currentPath);
      }
    }
  }
  
  extractErrors(errors);
  
  if (messages.length === 0) return;
  
  if (messages.length === 1) {
    toast.error(messages[0], { duration: 5000 });
    return;
  }
  
  toast.error(
    `Please fix ${messages.length} validation errors:\n${messages.slice(0, 3).join('\n')}${messages.length > 3 ? `\n...and ${messages.length - 3} more` : ''}`,
    {
      duration: 7000,
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
      },
    }
  );
}

// ========================================
// API ERROR TYPES
// ========================================

/**
 * Standard API error response structure from SBCLite backend
 */
interface ApiErrorResponse {
  success: false;
  error: string;
  details?: unknown;
}

interface ValidationDetail {
  path?: string[];
  message?: string;
  code?: string;
  field?: string;
}

function isValidationDetailArray(details: unknown): details is ValidationDetail[] {
  return Array.isArray(details);
}

function formatApiErrorDetails(details: unknown): string | null {
  if (!details) return null;

  if (isValidationDetailArray(details)) {
    if (details.length === 0) return null;

    const messages = details.map((d) => {
      if (d.path && d.path.length > 0) {
        return `${formatPath(d.path)}: ${d.message || 'Invalid value'}`;
      }
      if (d.field) {
        return `${d.field}: ${d.message || 'Invalid value'}`;
      }
      return d.message || 'Validation error';
    });

    return messages.length <= 3
      ? messages.join('\n')
      : `${messages.slice(0, 3).join('\n')}\n...and ${messages.length - 3} more`;
  }

  if (typeof details === 'object') {
    const entries = Object.entries(details as Record<string, unknown>).filter(([, value]) => value !== undefined);
    if (entries.length === 0) return null;

    return entries.map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value.map((item) => String(item)).join(', ')}`;
      }
      if (value && typeof value === 'object') {
        return `${key}: ${JSON.stringify(value)}`;
      }
      return `${key}: ${String(value)}`;
    }).join('\n');
  }

  return String(details);
}
/**
 * Check if an object matches the API error response structure
 */
function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'success' in data &&
    (data as ApiErrorResponse).success === false &&
    'error' in data &&
    typeof (data as ApiErrorResponse).error === 'string'
  );
}

// ========================================
// ERROR HANDLERS
// ========================================

/**
 * Shows a toast notification for API errors.
 * Extracts and displays error details from the response.
 */
export function showApiErrorToast(error: string, details?: ApiErrorResponse['details']): void {
  const formattedDetails = formatApiErrorDetails(details);

  if (!formattedDetails) {
    toast.error(error, { 
      duration: 5000,
      style: { maxWidth: '500px' },
    });
    return;
  }

  toast.error(
    `${error}\n\n${formattedDetails}`,
    {
      duration: 7000,
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
      },
    }
  );
}

/**
 * Universal error handler for form submission.
 * Catches ZodError, HTTP client errors (API errors), and other common errors.
 * 
 * @example
 * ```typescript
 * const onSubmit = async (data: FormData) => {
 *   try {
 *     await submitForm(data);
 *   } catch (error) {
 *     handleFormError(error);
 *   }
 * };
 * ```
 */
export function handleFormError(error: unknown): void {
  // Handle Zod validation errors
  if (error instanceof ZodError) {
    showZodErrorToast(error);
    return;
  }
  
  // Handle HTTP client errors (API responses)
  if (isHttpErrorLike(error)) {
    const responseData = error.response?.data;
    
    // Check if it's our standard API error format
    if (isApiErrorResponse(responseData)) {
      showApiErrorToast(responseData.error, responseData.details);
      return;
    }
    
    // Fallback to status text or generic message
    const statusText = error.response?.statusText || 'Request failed';
    const status = error.response?.status;
    toast.error(
      status ? `${status}: ${statusText}` : error.message,
      { duration: 5000 }
    );
    return;
  }
  
  // Handle generic Error objects
  if (error instanceof Error) {
    toast.error(error.message, { duration: 5000 });
    return;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    toast.error(error, { duration: 5000 });
    return;
  }
  
  // Handle unknown errors - show generic message to user
  // Note: Actual logging happens in createDebugErrorHandler for unexpected errors
  toast.error('An unexpected error occurred. Please try again.', { duration: 5000 });
}

/**
 * Checks if an error is an expected validation/user error (not a system error).
 * These errors should be shown to users but not logged to console.
 */
function isExpectedUserError(error: unknown): boolean {
  // Zod validation errors are expected user input issues
  if (error instanceof ZodError) {
    return true;
  }
  
  // API 4xx errors are expected (validation, auth, not found, etc.)
  if (isHttpErrorLike(error)) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500) {
      return true;
    }
  }
  
  return false;
}

/**
 * Creates an error handler that only logs unexpected errors to console.
 * Validation and API errors are shown to users via toast but not logged.
 */
export function createDebugErrorHandler(componentName: string) {
  return (error: unknown): void => {
    // Only log unexpected errors (5xx, network errors, unknown errors)
    // Validation errors (Zod, 4xx API) are expected and handled via toast
    if (!isExpectedUserError(error)) {
      console.error(`[${componentName}] Unexpected error:`, error);
    }
    handleFormError(error);
  };
}

// ========================================
// REACT HOOK FORM INTEGRATION
// ========================================

/**
 * Options for createFormErrorHandler
 */
export interface FormErrorHandlerOptions {
  /** Component name for debug logging */
  componentName?: string;
  /** Custom handler for specific error types */
  customHandler?: (error: unknown) => boolean; // Return true if handled
  /** Whether to show field errors on invalid submit */
  showFieldErrors?: boolean;
}

/**
 * Creates a comprehensive error handler for React Hook Form.
 * 
 * @example
 * ```typescript
 * const { handleSubmit, formState: { errors } } = useForm();
 * const errorHandler = createFormErrorHandler({
 *   componentName: 'SipPeerWizard',
 *   showFieldErrors: true,
 * });
 * 
 * // In form:
 * <form onSubmit={handleSubmit(onSubmit, errorHandler.onInvalid)}>
 * 
 * // In submit handler:
 * const onSubmit = async (data) => {
 *   try {
 *     await api.create(data);
 *   } catch (error) {
 *     errorHandler.onError(error);
 *   }
 * };
 * ```
 */
export function createFormErrorHandler(options: FormErrorHandlerOptions = {}) {
  const { componentName = 'Form', customHandler, showFieldErrors = true } = options;
  
  const debugHandler = createDebugErrorHandler(componentName);
  
  return {
    /**
     * Handler for React Hook Form's onInvalid callback.
     * Called when form validation fails before submit.
     * Note: No console logging - validation errors are expected user input issues.
     */
    onInvalid: (errors: FieldErrors): void => {
      if (showFieldErrors) {
        showFieldErrorsToast(errors);
      }
    },
    
    /**
     * Handler for errors during form submission.
     * Use in try/catch around your submit logic.
     */
    onError: (error: unknown): void => {
      // Try custom handler first
      if (customHandler && customHandler(error)) {
        return;
      }
      debugHandler(error);
    },
  };
}
